import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import { NutritionAssignment } from '@emm/core/models/nutrition.model';
import { NutritionFacadeService } from '@emm/management/services/nutrition-facade.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationSet } from '@emm/core/models/location-set.model';
import { MatTableDataSource } from '@angular/material/table';
import { FlagKeys, LaunchDarklyService } from '@emm/core/services/launch-darkly.service';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-nutrition-assignments-form',
    styleUrls: ['./nutrition-assignments-form.component.scss'],
    templateUrl: './nutrition-assignments-form.component.html'
})
export class NutritionAssignmentsFormComponent implements OnInit {

    @Output() formStateChanged = new EventEmitter<{ disableCancel: boolean, disableSave: boolean }>();

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    @Input() locationSet: LocationSet;
    canEdit: boolean;
    location: string;
    profile: number | null | string;
    previousAssignment: NutritionAssignment;
    profileOptions$ = this.nutritionFacade.profileOptions$;
    selectedDate: Date;
    displayedColumns = ['nutritionProfile', 'locationLevel', 'effectiveDate'];
    dataSource = new MatTableDataSource<any>();
    showHierarchyTableToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.SHOW_HIERARCHY_TABLE);

    dateClass = (d: Date): string => {
        let anyProfileMatchesDate = false;
        this.nutritionFacade.assignments$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(profiles => {
                anyProfileMatchesDate = profiles
                    .map(p => new Date(p.effectiveDate))
                    .filter(profileDate => profileDate.toDateString() === d.toDateString()).length !== 0;
            })
        return anyProfileMatchesDate ? 'highlight-date' : '';
    }

    constructor(private nutritionFacade: NutritionFacadeService, private launchDarklyService: LaunchDarklyService) {}

    ngOnInit() {
        this.canEdit = this.isSuperAdmin || this.isAdmin;
        this.selectedDate = new Date();
        this.profile = null;
        this.previousAssignment = null;

        this.nutritionFacade.assignment$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(assignment => {
                if (!assignment || !assignment.profile) {
                    this.selectedDate = new Date();
                    this.profile = null;
                    this.changeFormReadyTo(false);
                    return;
                }

                const profileHierarchyAsDataSource = [];
                assignment.profiles.profileHierarchyDetails.forEach(pf => {
                    const profile = {
                        nutritionProfile: pf.profile,
                        locationName: pf.locationName,
                        locationId: pf.locationId,
                        effectiveDate: pf.effectiveDate
                    };
                    profileHierarchyAsDataSource.push(profile);
                });

                this.dataSource.data = profileHierarchyAsDataSource;
                const lastIndex = profileHierarchyAsDataSource.length - 1;
                this.selectedDate = new Date(assignment.effectiveDate);
                this.profile = profileHierarchyAsDataSource[lastIndex].nutritionProfile ?? 'Not Set';

                this.previousAssignment = {
                    effectiveDate: assignment.effectiveDate,
                    profile: assignment.profile,
                    profileHierarchy: assignment.profileHierarchy
                };

                this.changeFormReadyTo(false);

                this.nutritionFacade.selectedDate(this.selectedDate.getTime());
        });

        this.nutritionFacade.location$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(location => {
            this.location = location && location.tag;
        });

    }

    cancel(): void {
        if (this.previousAssignment) {
            this.selectedDate = new Date(this.previousAssignment.effectiveDate);
            this.profile = this.previousAssignment.profile;
        } else {
            this.selectedDate = new Date();
            this.profile = null;
        }
        this.changeFormReadyTo(false);
    }

    submit(): void {
        this.changeFormReadyTo(false);
        const date =
            this.selectedDate.getFullYear() +
            '-' +
            (this.selectedDate.getMonth() >= 9 ? (this.selectedDate.getMonth() + 1) : '0' + (this.selectedDate.getMonth() + 1)) +
            '-' +
            (this.selectedDate.getDate() >= 10 ? this.selectedDate.getDate() : '0' + this.selectedDate.getDate())
        ;
        this.nutritionFacade.saveLocationAssignment(
            {
                dateShort: date,
                effectiveDate: this.selectedDate.getTime(),
                location: this.location,
                profile: this.profile === 'Not Set' ? null : this.profile,
                profileHierarchy: null
            }
        );
    }

    handleDateChange(): void {
        this.changeFormReadyTo(true);
        this.nutritionFacade.selectedDate(this.selectedDate.getTime());
        this.nutritionFacade.assignments$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(profiles => {
                const closestDateBefore = this.getClosestDateOnOrBefore(this.selectedDate, profiles.map(p => new Date(p.effectiveDate)));
                this.profile = profiles.find(
                    p => new Date(p.effectiveDate).toDateString() === closestDateBefore.toDateString()
                    ).profile ?? this.profile;
            })
    }

    changeFormReadyTo(isReady: boolean) {
        const today = new Date();
        today.setHours(0,0,0,0);
        const testDate = new Date(this.selectedDate);
        const isDatePassed = today.getTime() > testDate.getTime();
        this.formStateChanged.next(
            {
                disableCancel: !isReady,
                disableSave: !isReady || isDatePassed
            }
        );
    }

    private getClosestDateOnOrBefore(targetDate: Date, dates: Date[]) {
        const filteredDates = dates.filter(date => date <= targetDate);
        if (filteredDates.length === 0) return null;
        const closestDate = filteredDates.reduce((closest, current) => {
            const closestDiff = targetDate.getTime() - closest.getTime();
            const currentDiff = targetDate.getTime() - current.getTime();
            return currentDiff < closestDiff ? current : closest;
        });
        return closestDate;
    }
}
